"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"
import { X } from "lucide-react"
import { Dialog } from "radix-ui"

import { cn } from "@/lib/utils"
import { Reveal } from "@/components/ui/motion"
import { useTranslations } from "next-intl"

/**
 * The app's primary navigation, hung off the wordmark.
 *
 * The header's middle zone belongs to whichever page is mounted — the reader puts
 * a row of panel tabs and a split toggle there, the canvas up to five graph
 * controls — so global nav cannot live in it without fighting for width on
 * exactly the pages that need the width most. The left zone is fixed and holds
 * only the wordmark, so the nav goes there and costs nothing: the trigger is a
 * word that was already on screen.
 *
 * The panel covers the viewport rather than sitting in a rail. There are three
 * destinations, so a half-width sheet would be mostly empty; and inverting the
 * page (foreground ground, background type) makes it unmistakably a mode you are
 * in rather than a menu grazing the edge of the page you were reading.
 *
 * Built on Dialog directly rather than components/ui/sheet, whose SheetContent is
 * a padded, half-width, shadowed, `bg-background` panel — every one of which this
 * would have to override. The shared sheet is right for what it is used for; this
 * is a different surface that happens to slide.
 */

/**
 * Order is order of use: research is where you land, propositions is the other way
 * into the corpus, bookmarks is what you have accumulated.
 *
 * "Propositions" rather than "Explore" — the page is /explore, but the thing it
 * shows has a name (see FEATURES.propositions in config/features.ts) and naming
 * the content says more than naming the verb.
 *
 * No locks here, unlike the old sidebar's rows: /explore and /bookmarks are both
 * public routes that show non-subscribers a teaser (see config/public-routes.ts),
 * so there is nothing to warn anyone off.
 */
const NAV = [
  { label: "Research", href: "/research" },
  { label: "Propositions", href: "/explore" },
  { label: "Bookmarks", href: "/bookmarks" },
] as const

/** The wordmark itself, drawn identically wherever it appears. */
function Mark({ className }: { className?: string }) {
  const t = useTranslations("nav.WordmarkMenu")
  return (
    <span className={cn("text-2xl font-bold leading-none whitespace-nowrap", className)}>
      {t("lawlibraryAi")}
    </span>
  )
}

export function WordmarkMenu({ className }: { className?: string }) {
  const t = useTranslations("nav")
  const pathname = usePathname()

  return (
    <Dialog.Root>
      <Dialog.Trigger
        className={cn(
          "cursor-pointer opacity-100 transition-opacity hover:opacity-70",
          className,
        )}
      >
        <Mark />
      </Dialog.Trigger>

      <Dialog.Portal>
        {/*
          No overlay: the panel is the full viewport, so an overlay would be a
          layer nobody can see or click. Radix locks the scroll from the content
          node itself, and Escape still closes.

          `aria-describedby={undefined}` because there is no description to point
          at — without it Radix warns on every open.
        */}
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            "fixed inset-0 z-50 flex flex-col bg-foreground text-background",
            "data-[state=open]:animate-in data-[state=open]:slide-in-from-left data-[state=open]:duration-500",
            "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=closed]:duration-300",
          )}
        >
          <Dialog.Title className="sr-only">{t("menu")}</Dialog.Title>

          {/*
            The header band, repeated. Same height and same gutters as AppHeader,
            so the wordmark lands exactly where the one it replaced was standing
            and the panel reads as the page turning over rather than as a second
            screen arriving. It is also the close control: whatever opened this is
            what shuts it, in the place the pointer already is.
          */}
          <div className="flex h-16 shrink-0 flex-row items-center justify-between px-6 md:px-12">
            <Dialog.Close className="cursor-pointer opacity-100 transition-opacity hover:opacity-70">
              <Mark />
            </Dialog.Close>
            <Dialog.Close
              aria-label={t("closeMenu")}
              className="cursor-pointer opacity-60 transition-opacity hover:opacity-100"
            >
              <X className="size-6" />
            </Dialog.Close>
          </div>

          <nav className="flex flex-1 flex-col justify-center px-6 pb-16 md:px-12">
            <ul className="flex flex-col gap-2 md:gap-4">
              {NAV.map((item, index) => {
                const active = pathname === item.href

                return (
                  // Staggered with the house cascade, delayed past the slide so the
                  // words arrive on a panel that has stopped moving — animating them
                  // while it travels reads as two movements fighting.
                  <Reveal as="li" key={item.href} index={index} delay={0.35}>
                    {/*
                      Close on click rather than on the route changing: a slow route
                      would otherwise leave the panel sitting open over the page it
                      had already asked for, looking stuck.
                    */}
                    <Dialog.Close asChild>
                      <Link
                        href={item.href}
                        className={cn(
                          "flex w-fit flex-row items-center gap-4 font-bold tracking-tight transition-opacity md:gap-6",
                          "text-5xl sm:text-6xl md:text-7xl lg:text-8xl",
                          active ? "opacity-100" : "opacity-60 hover:opacity-100",
                        )}
                      >
                        {/*
                          Where you already are. A dot rather than a colour, since the
                          panel has exactly two of those.

                          Drawn for every item and merely hidden on the rest, rather
                          than rendered only when active: a marker that exists only on
                          one row would indent that row's word and leave the three of
                          them starting at three different places.
                        */}
                        <span
                          className={cn(
                            "size-3 shrink-0 rounded-full bg-background md:size-4 lg:size-5",
                            !active && "opacity-0",
                          )}
                        />
                        {item.label}
                      </Link>
                    </Dialog.Close>
                  </Reveal>
                )
              })}
            </ul>
          </nav>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
